import { Injectable, PLATFORM_ID, Inject, OnDestroy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class DeviceDetectionService implements OnDestroy {
    private isMobileDevice: boolean | null = null;
    private resizeSubject = new Subject<void>();
    private resizeListener?: () => void;

    constructor(@Inject(PLATFORM_ID) private platformId: Object) {
        if (isPlatformBrowser(this.platformId)) {
            this.detectDevice();
            this.setupResizeListener();
        }
    }

    private setupResizeListener(): void {
        if (typeof window !== 'undefined') {
            // Debounce resize events to avoid excessive checks
            this.resizeSubject.pipe(debounceTime(100)).subscribe(() => {
                this.detectDevice();
            });

            this.resizeListener = () => {
                this.resizeSubject.next();
            };

            window.addEventListener('resize', this.resizeListener);
        }
    }

    private detectDevice(): void {
        if (typeof window !== 'undefined') {
            // Check if it's a mobile device based on screen width and user agent
            const isMobileWidth = window.innerWidth <= 768;
            const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                navigator.userAgent
            );
            this.isMobileDevice = isMobileWidth || isMobileUserAgent;
        }
    }

    ngOnDestroy(): void {
        if (this.resizeListener && typeof window !== 'undefined') {
            window.removeEventListener('resize', this.resizeListener);
        }
        this.resizeSubject.complete();
    }

    isMobile(): boolean {
        if (this.isMobileDevice === null && typeof window !== 'undefined') {
            this.detectDevice();
        }
        return this.isMobileDevice ?? false;
    }

    isDesktop(): boolean {
        return !this.isMobile();
    }

    getImageWidth(): number {
        // Return appropriate width based on device type
        // Mobile: 200px, Desktop: 400px (or higher based on requirements)
        return this.isMobile() ? 200 : 400;
    }
}

